import React, { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit, MdImage } from "react-icons/md";
import MainModal from "components/Modal/MainModal";
import { toast } from "react-toastify";
import { adminApiService } from "../../../services/api";

const ProductRecommend = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBgModalOpen, setIsBgModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    productName: "",
    price: "",
    productLink: "",
    image: null,
  });
  const [bgFormData, setBgFormData] = useState({
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [bgImagePreview, setBgImagePreview] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleBgImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgFormData((prev) => ({ ...prev, image: file }));
      setBgImagePreview(URL.createObjectURL(file));
    }
  };

  const fetchRecommendations = async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getProductRecommendations();
      if (!res.ok) throw new Error("Failed to fetch recommendations");
      const { data } = await res.json();
      console.log("Recommendations data:", data);

      // Ensure data is always an array
      if (Array.isArray(data)) {
        setRecommendations(data);
      } else if (data && Array.isArray(data.recommendations)) {
        setRecommendations(data.recommendations);
      } else {
        console.warn("API returned non-array data:", data);
        setRecommendations([]);
      }
    } catch (error) {
      toast.error("Error fetching recommendations.");
      console.error("Fetch error:", error);
      setRecommendations([]); // Ensure it's an array even on error
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBackgroundImage = async () => {
    try {
      const res = await adminApiService.getProductRecommendationsBg();
      if (!res.ok) throw new Error("Failed to fetch background image");
      const { data } = await res.json();
      console.log("Background image data:", data); // Debug log
      setBackgroundImage(data || null);
      if (data?.background_image_url) {
        setBgImagePreview(
          adminApiService.getProductRecommendationImageUrl(
            data.background_image_url
          )
        );
      }
    } catch (error) {
      console.error("Error fetching background image:", error);
      toast.error("Error fetching background image.");
      setBackgroundImage(null); // Ensure it's null on error
    }
  };

  useEffect(() => {
    fetchRecommendations();
    fetchBackgroundImage();
  }, []);

  const handleDelete = async (recommendation) => {
    if (
      !window.confirm(`Are you sure you want to delete this recommendation?`)
    ) {
      return;
    }

    try {
      const res = await adminApiService.deleteProductRecommendation(
        recommendation.id
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete recommendation");
      }

      toast.success("Recommendation deleted successfully!");
      await fetchRecommendations();
    } catch (error) {
      toast.error(`Error deleting recommendation: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  const openAddModal = () => {
    setIsAddMode(true);
    setEditingRecommendation(null);
    setFormData({
      title: "",
      productName: "",
      price: "",
      productLink: "",
      image: null,
    });
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const openEditModal = (recommendation) => {
    setIsAddMode(false);
    setEditingRecommendation(recommendation);
    setFormData({
      title: recommendation.title,
      productName: recommendation.product_name,
      price: recommendation.price,
      productLink: recommendation.product_link,
      image: null,
    });
    setImagePreview(
      adminApiService.getProductRecommendationImageUrl(recommendation.image_url)
    );
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (
      !formData.title.trim() ||
      !formData.productName.trim() ||
      !formData.price.trim() ||
      !formData.productLink.trim()
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title.trim());
      formDataToSend.append("productName", formData.productName.trim());
      formDataToSend.append("price", formData.price.trim());
      formDataToSend.append("productLink", formData.productLink.trim());
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }
      if (!isAddMode) {
        formDataToSend.append("id", editingRecommendation.id);
      }

      const res = isAddMode
        ? await adminApiService.addProductRecommendation(formDataToSend)
        : await adminApiService.updateProductRecommendation(formDataToSend);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to ${isAddMode ? "add" : "update"} recommendation`
        );
      }

      const responseData = await res.json();
      console.log(responseData);

      toast.success(
        `Recommendation ${isAddMode ? "added" : "updated"} successfully!`
      );
      setIsModalOpen(false);
      await fetchRecommendations();
      setFormData({
        title: "",
        productName: "",
        price: "",
        productLink: "",
        image: null,
      });
      setImagePreview(null);
      setEditingRecommendation(null);
      setIsAddMode(false);
    } catch (error) {
      toast.error(
        `Error ${isAddMode ? "adding" : "updating"} recommendation: ${
          error.message
        }`
      );
      console.error("Submit error:", error);
    }
  };

  const handleBgSubmit = async () => {
    if (!bgFormData.image) {
      toast.error("Please select an image.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("image", bgFormData.image);
      if (backgroundImage?.id) {
        formDataToSend.append("id", backgroundImage.id);
      }


      const res = await adminApiService.updateProductRecommendationBg(
        formDataToSend
      );

      // Check if the response is JSON
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error(
          "Server returned non-JSON response. Please check the API endpoint."
        );
      }

      const responseData = await res.json();
      console.log("Response data:", responseData);

      if (!res.ok) {
        throw new Error(
          responseData.message || "Failed to update background image"
        );
      }

      toast.success("Background image updated successfully!");
      setIsBgModalOpen(false);
      fetchRecommendations();
      setBgFormData({ image: null });
      setBgImagePreview(null);
    } catch (error) {
      console.error("Background image update error:", error);
      if (error.message.includes("non-JSON response")) {
        toast.error(
          "Server error. Please check if the API endpoint is correct."
        );
      } else {
        toast.error(
          error.message || "Error updating background image. Please try again."
        );
      }
    }
  };

  const openBgModal = () => {
    if (backgroundImage?.background_image_url) {
      setBgImagePreview(
        `${process.env.REACT_APP_API_URL_Plain}/config/uploads/product_recommendations/${backgroundImage.background_image_url}`
      );
    }
    setIsBgModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Product Recommendations
              </h1>
              <p className="mt-3 text-base text-gray-600">
                Manage product recommendations displayed on the website
              </p>
            </div>
            <div className="flex space-x-4">
              {recommendations.length === 0 && (
                <>
                  <button
                    onClick={openAddModal}
                    className="inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    <MdAdd className="mr-2" size={24} />
                    Add New Recommendation
                  </button>
                  <button
                    onClick={() => setIsBgModalOpen(true)}
                    className="inline-flex items-center rounded-xl bg-gray-600 px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-gray-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    <MdImage className="mr-2" size={24} />
                    Manage Background
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-primary"></div>
            </div>
          ) : recommendations.length === 0 ? (
            <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
              <div className="rounded-full bg-primary/10 p-6">
                <MdAdd className="h-16 w-16 text-primary" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No Recommendations Available
              </h3>
              <p className="mt-3 text-base text-gray-500">
                Get started by adding your first product recommendation.
              </p>
              <button
                onClick={openAddModal}
                className="mt-8 inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <MdAdd className="mr-2" size={24} />
                Add New Recommendation
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {Array.isArray(recommendations) &&
                  recommendations.map((recommendation) => (
                    <div
                      key={recommendation.id}
                      className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-lg"
                    >
                      <div className="flex flex-col">
                        <img
                          src={adminApiService.getProductRecommendationImageUrl(
                            recommendation.image_url
                          )}
                          alt={recommendation.title}
                          className="mb-6 h-56 w-full rounded-xl object-cover"
                        />
                        <h3 className="text-xl font-semibold text-gray-900">
                          {recommendation.title}
                        </h3>
                        <p className="mt-2 text-base font-medium text-primary">
                          {recommendation.product_name}
                        </p>
                        <p className="mt-2 text-base text-gray-600">
                          Price: TK{recommendation.price}
                        </p>
                        <a
                          href={recommendation.product_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 text-base text-primary hover:text-primaryDark"
                        >
                          View Product
                        </a>
                        <div className="mt-6 flex items-center justify-end space-x-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <button
                            onClick={() => openEditModal(recommendation)}
                            className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                            aria-label="Edit recommendation"
                          >
                            <MdEdit size={24} />
                          </button>
                          <button
                            onClick={() => handleDelete(recommendation)}
                            className="rounded-xl p-2.5 text-red-600 transition-colors hover:bg-red-50"
                            aria-label="Delete recommendation"
                          >
                            <MdDelete size={24} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="mt-12">
                <h2 className="mb-6 text-2xl font-semibold text-gray-900">
                  Background Image
                </h2>
                {backgroundImage?.background_image_url ? (
                  <div className="group relative overflow-hidden rounded-2xl">
                    <img
                      src={adminApiService.getProductRecommendationImageUrl(
                        backgroundImage.background_image_url
                      )}
                      alt="Background"
                      className="h-64 w-full object-cover"
                    />
                    <div className="bg-black/50 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <button
                        onClick={openBgModal}
                        className="rounded-xl bg-white px-6 py-3 text-base font-medium text-gray-900 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
                      >
                        Edit Background
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-64 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center">
                    <div className="rounded-full bg-primary/10 p-6">
                      <MdImage className="h-16 w-16 text-primary" />
                    </div>
                    <p className="mt-6 text-base text-gray-500">
                      No background image set
                    </p>
                    <button
                      onClick={openBgModal}
                      className="mt-6 rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md"
                    >
                      Upload Background
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <MainModal
        isOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
        title={
          isAddMode ? "Add New Recommendation" : "Edit Product Recommendation"
        }
        btnTitle={isAddMode ? "Add Recommendation" : "Update Recommendation"}
        actionbtn={handleSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="title"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter recommendation title..."
            />
          </div>
          <div>
            <label
              htmlFor="productName"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Product Name *
            </label>
            <input
              type="text"
              id="productName"
              name="productName"
              value={formData.productName}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter product name..."
            />
          </div>
          <div>
            <label
              htmlFor="price"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Price *
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter product price..."
              step="0.01"
              min="0"
            />
          </div>
          <div>
            <label
              htmlFor="productLink"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Product Link *
            </label>
            <input
              type="url"
              id="productLink"
              name="productLink"
              value={formData.productLink}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter product link..."
            />
          </div>
          <div>
            <label
              htmlFor="image"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Product Image
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleImageChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              accept="image/*"
            />
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-40 w-40 rounded-xl object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </MainModal>

      {/* Background Image Modal */}
      <MainModal
        isOpen={isBgModalOpen}
        setModalOpen={setIsBgModalOpen}
        title="Update Background Image"
        btnTitle="Update Image"
        actionbtn={handleBgSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="bgImage"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Background Image
            </label>
            <input
              type="file"
              id="bgImage"
              name="image"
              onChange={handleBgImageChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              accept="image/*"
            />
            {bgImagePreview && (
              <div className="mt-4">
                <img
                  src={bgImagePreview}
                  alt="Preview"
                  className="h-64 w-full rounded-xl object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </MainModal>
    </div>
  );
};

export default ProductRecommend;
